// Everything the verification panel decides, as pure functions.
//
// WHY THIS FILE EXISTS AT ALL. `frontend/vitest.config.ts` is
// `environment: 'node'` with `include: ['src/**/*.test.ts']`, and this repo
// has no jsdom, no happy-dom and no @testing-library/react — every test file
// here is pure logic. A `.tsx` render test would not be *collected*, so it
// would report a pass having run nothing, which is worse than no test at all.
// Rather than smuggle a component-testing stack into a ticket about a panel,
// the panel's decisions live here and are tested in
// `__tests__/panelModel.test.ts`; `VerificationPanel.tsx` is then wiring thin
// enough to read, and is exercised for real in T19's browser walkthrough.
//
// NOTHING HERE CAN STOP PLAYBACK, and nothing here reads the chain. It takes
// a ContainerRecord that has already been read and projected
// (useContainerRecord.ts) and turns it into rows and, at most, a warning. The
// warning is a warning by construction: `panelWarning` returns text, and text
// has no path into `applyVerificationOutcome`.
import type { ContainerRecord, VersionConsistency } from './useContainerRecord.js'

/** One version, as the panel draws it. */
export interface PanelRow {
  /** The chain's own index, zero-based, as the VersionRef holds it. */
  index: number
  /** Human label, counting from one — the same "v1"/"v2" the admin version
   * list uses (VersionsSection.tsx), so a publisher and a viewer are looking
   * at the same names for the same things. */
  label: string
  videoId: string
  /** The Video account this version's own root hash lives on. A viewer
   * following the trail reads this next, so the panel links to it. */
  videoPda: string
  /** The chain's clock, off the VersionRef account — never a local one. */
  addedAt: Date
  /** Exactly one row at most: the version being played. */
  watching: boolean
  /** The tail ref, decided by the container's own `versionCount`. */
  current: boolean
  /** This version's bytes were deleted. Off-chain knowledge — the chain has
   * no concept of deletion, so it can only ever arrive as an argument here.
   * What's guaranteed is narrower than "every on-chain row is kept and
   * stamped, never removed": the store decides off `ContainerVersion.Chain`,
   * its own local record of a VersionRef it was TOLD about
   * (RecordVersionAnchor) — not off a chain read. An appended-but-unreported
   * tail (a VersionRef that landed on chain but whose report back to the
   * store never did) can still be `planRemove`'d from the container JSON
   * entirely, even while `panelRows` — built from decoded on-chain refs —
   * keeps drawing a row for it. In that window this flag simply reads false;
   * it is not wrong, the entry is just no longer in the JSON to be found. */
  deleted: boolean
  /** Only present for versions the caller has actually read a root hash for.
   * A page has read the chain record of the video it is PLAYING and of
   * nothing else, so every other row is null rather than blank-but-hopeful. */
  rootHash: string | null
}

export interface PanelWarning {
  title: string
  detail: string
}

const normalize = (id: string): string => id.trim().toLowerCase()

/**
 * The version list the panel renders.
 *
 * Returns an empty list — no version section at all — when the chain has not
 * been read, and when the container holds a single version. The second case
 * is the overwhelmingly common one and it has no story to tell: a video that
 * has been published once looks exactly as it did before versions existed.
 * "Single" is judged on BOTH the decoded refs and the container's claimed
 * `versionCount`, so a container claiming three versions of which one ref
 * decoded still renders its list — that disagreement is information, and
 * hiding it behind a "looks like one version" test would be the one case
 * where suppressing the list actively misleads.
 *
 * `deletedVideoIds` and `rootHashes` are both knowledge the chain does not
 * hold, supplied by whoever does: the API's container JSON for deletions, and
 * the playing video's own chain record for its root hash.
 */
export function panelRows(
  record: ContainerRecord | null,
  playingVideoId: string,
  deletedVideoIds: Iterable<string> = [],
  rootHashes: Record<string, string> = {},
): PanelRow[] {
  if (!record) return []
  if (record.versions.length <= 1 && record.versionCount <= 1) return []

  const deleted = new Set<string>()
  for (const id of deletedVideoIds) deleted.add(normalize(id))

  const hashes = new Map<string, string>()
  for (const [id, hash] of Object.entries(rootHashes)) hashes.set(normalize(id), hash)

  const playing = normalize(playingVideoId)
  // `record.versions` is already ascending by index, but sorting a copy here
  // keeps this function's contract independent of that one's.
  const ordered = [...record.versions].sort((a, b) => a.index - b.index)

  // Exactly one watching marker, even in the pathological case of a container
  // listing the same video twice: the first match wins and the rest are
  // ordinary rows. Two "you are here" markers is not a state a viewer can
  // make sense of.
  let watched = false

  return ordered.map((version) => {
    const id = normalize(version.videoId)
    const watching = !watched && id === playing
    if (watching) watched = true
    return {
      index: version.index,
      label: `v${version.index + 1}`,
      videoId: version.videoId,
      videoPda: version.videoPda,
      addedAt: version.addedAt,
      watching,
      // The chain's claim, not `ordered.length - 1`: a ref that failed to
      // decode must not promote the version before it into looking current.
      current: version.index === record.versionCount - 1,
      deleted: deleted.has(id),
      rootHash: hashes.get(id) ?? null,
    }
  })
}

/**
 * The one warning the version axis can raise, and the only consistency state
 * that earns any words at all.
 *
 * `consistent` needs no warning. `unanchored` is the ordinary, unremarkable
 * state most videos are in — the chain holds no container for this video, and
 * that is not a fault. `unknown` means we did not get an answer, and inventing
 * a warning out of a network blip would be a claim about the publisher rather
 * than about us.
 *
 * The wording is load-bearing, and it is load-bearing twice over here.
 *
 * A SUPERSEDED version's bytes hashed correctly against the root its
 * publisher sealed; they are authentic and playback is untouched (see
 * `withVersionConsistency`). That warning says a newer version exists, and
 * must never imply that what is playing failed anything.
 *
 * An UNLISTED version is a different fact and gets different words. The bytes
 * still hashed correctly — the player checked them against the root hash on
 * the video's own chain record, and that passed. What is missing is the
 * container's acknowledgement: the chain's list of versions does not include
 * this one. That is usually innocent, and the wording says so: a version can
 * be published and simply not appended yet. But it is also the only signal a
 * viewer would ever get if the list and what they were served genuinely
 * disagreed, which is why it can never be silent. Saying nothing here was the
 * hole this replaced.
 */
export function panelWarning(consistency: VersionConsistency): PanelWarning | null {
  if (consistency === 'superseded') {
    return {
      title: 'A newer version of this video has been published',
      detail:
        'What you are watching is authentic — it matches the version its publisher sealed on chain — but the chain lists a later version as the current one.',
    }
  }
  if (consistency === 'unlisted') {
    return {
      title: "This version is not in the chain's list of versions",
      detail:
        'The video you are watching matches the record its publisher anchored on chain, so its content is authentic. The chain also holds a list of this video\'s versions, and this one is not on it — which usually means it was published but not yet added to the list.',
    }
  }
  return null
}
